import React, { useCallback, useEffect } from 'react';
import { Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useRouting } from '@alycecom/hooks';

import { getIsClaimed, getIsLoading, getRedirectUrl } from '../../../store/claim/claim.selectors';
import { getGiftCustomizing, getIsGift } from '../../../store/data/data.selectors';
import SuccessModal from '../../../../../components/SuccessModal/SuccessModal';
import { setRedirect } from '../../../store/redirect/redirect.actions';

export interface ICallToActionModalProps {
  parentUrl: string;
}

const CallToActionModal = ({ parentUrl }: ICallToActionModalProps): JSX.Element => {
  const go = useRouting();
  const dispatch = useDispatch();

  const isLoading = useSelector(getIsLoading);
  const isGiftClaimed = useSelector(getIsClaimed);
  const codeRedirectUrl = useSelector(getRedirectUrl);
  const isGift = useSelector(getIsGift);

  const customization = useSelector(getGiftCustomizing);

  const isRedirectRequired = !!customization.redirectUrl;
  const isRedirectConfirmed = isRedirectRequired && customization.redirectConfirm;
  const giftThankYouUrl = isGift ? `${parentUrl}/thank-you` : codeRedirectUrl;
  const redirectUrl = customization.redirectUrl || giftThankYouUrl;

  const handleSubmit = () => {
    window.location.href = redirectUrl;
  };

  const handleDiscard = useCallback(() => {
    if (isGift) {
      go(giftThankYouUrl);
    } else {
      dispatch(
        setRedirect({
          message: 'You will be automatically redirected to the thank you page in a moment',
          url: codeRedirectUrl,
        }),
      );
    }
  }, [go, codeRedirectUrl, giftThankYouUrl, isGift, dispatch]);

  useEffect(() => {
    if (isGiftClaimed && isRedirectRequired && !isRedirectConfirmed) {
      dispatch(setRedirect({ url: redirectUrl, timeout: 3000 }));
    }
  }, [isGiftClaimed, dispatch, redirectUrl, isRedirectConfirmed, isRedirectRequired, handleDiscard]);

  return (
    <SuccessModal
      title={customization.redirectHeader || ''}
      isOpen={isGiftClaimed && isRedirectConfirmed}
      icon="share-square"
      isLoading={isLoading}
      onSubmit={handleSubmit}
      onDiscard={handleDiscard}
      submitButtonText={customization.redirectButton || ''}
    >
      <Typography data-testid="customRedirectMessage" className="Body-Regular-Left-Static">
        {customization.redirectMessage}
      </Typography>
    </SuccessModal>
  );
};

export default CallToActionModal;
